import { useCallback } from "react";
import { persistHomeChatMatch } from "../utils/homeChatMatchPersistence";
import {
  persistMatchProfile,
  persistWizardAnswersFromChat,
} from "../utils/matchScoreUtils";
import { syncWizardFromFeedMatch } from "../utils/resolveMatchIntent";
import {
  FEED_TAB_PARAM,
  MATCH_SESSION_PARAM,
} from "../pages/feed/feedConstants";

/**
 * Shared handler for AI chat match results on home and feed surfaces.
 */
export default function useHomeChatMatchResults({
  activityUserId,
  loadActiveFeedMatch,
  setChatMatchLoading,
  setChatMatchSearchParams,
  setChatMatchProducts,
  setMatchScoreRevision,
  setChatExpanded,
  variant = "home",
  searchParams,
  setSearchParams,
  onAfterApply,
}) {
  return useCallback(
    async ({
      products,
      searchParams: intentParams,
      matchProfile,
      sessionId,
      noResults = false,
      resultKey = null,
    }) => {
      if (variant === "home" && noResults) {
        setChatMatchSearchParams(intentParams || null);
        return;
      }

      setChatMatchLoading(true);
      try {
        setChatMatchSearchParams(intentParams || null);

        if (variant === "home") {
          syncWizardFromFeedMatch({ searchParams: intentParams, matchProfile });
        } else if (matchProfile) {
          persistMatchProfile(matchProfile);
        } else if (intentParams) {
          persistWizardAnswersFromChat(intentParams);
        }

        setMatchScoreRevision((n) => n + 1);
        setChatExpanded(false);

        if (variant === "feed" && sessionId && setSearchParams) {
          const next = new URLSearchParams(searchParams);
          next.set(FEED_TAB_PARAM, "matches");
          next.set(MATCH_SESSION_PARAM, sessionId);
          setSearchParams(next, { replace: true });
        }

        let resolvedProducts = products || [];
        let resolvedSearchParams = intentParams || null;
        let resolvedMatchProfile = matchProfile || null;
        let resolvedSessionId = sessionId || null;
        let serverMatch = null;

        if (sessionId && activityUserId) {
          serverMatch = await loadActiveFeedMatch(sessionId);
          if (serverMatch?.products?.length) {
            resolvedProducts = serverMatch.products;
            resolvedSearchParams =
              serverMatch.searchParams ?? resolvedSearchParams;
            resolvedMatchProfile =
              serverMatch.matchProfile ?? resolvedMatchProfile;
            resolvedSessionId = serverMatch.sessionId ?? resolvedSessionId;

            if (variant === "home") {
              syncWizardFromFeedMatch(serverMatch);
            } else {
              if (serverMatch.matchProfile) {
                persistMatchProfile(serverMatch.matchProfile);
              }
              if (serverMatch.searchParams) {
                persistWizardAnswersFromChat(serverMatch.searchParams);
              }
            }
          }
        }

        setChatMatchProducts(resolvedProducts);
        setChatMatchSearchParams(resolvedSearchParams);
        persistHomeChatMatch({
          sessionId: resolvedSessionId,
          products: resolvedProducts,
          searchParams: resolvedSearchParams,
          matchProfile: resolvedMatchProfile,
          resultKey,
          ownerUserId: activityUserId || null,
        });

        onAfterApply?.({
          sessionId: resolvedSessionId,
          products: resolvedProducts,
          searchParams: resolvedSearchParams,
          serverMatch,
        });
      } finally {
        setChatMatchLoading(false);
      }
    },
    [
      activityUserId,
      loadActiveFeedMatch,
      onAfterApply,
      searchParams,
      setChatExpanded,
      setChatMatchLoading,
      setChatMatchProducts,
      setChatMatchSearchParams,
      setMatchScoreRevision,
      setSearchParams,
      variant,
    ],
  );
}
